import React, {useCallback, useMemo, useState} from "react";
import {AccountingTransactionTypes, CustomerPaymentTerms} from "../../../../../../../../../core/constants/enums";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import classnames from "classnames";
import {ReactComponent as RefundIcon} from "../../../../../../../../../assets/images/credit-card-refund.svg";
import SummaryActionCard from "../../../../../../../../components/app-specific/summary-cards/summary-action-card";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import OrderBypassInfoCard from "../../../../../../../../components/app-specific/order-inforamtion-shared-sections/bypass-card";
import AddCreditToOrderDialog from "../../../../../../../../components/dialogs/add-credit-to-order";
import AddGiftCardToOrderDialog from "../../../../../../../../components/dialogs/add-gift-card-to-order";
import Api from "../../../../../../../../../core/services/api/api";
import PayForOrderDialog from "../../../../../../../../components/dialogs/pay-for-order";
import {CircularProgress} from "@mui/material";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {useParams} from "react-router-dom";
import {useAppSelector} from "../../../../../../../../redux";
import TransactionReceiptDialog from "../../../../../../../../components/dialogs/transaction-receipt-dialog";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {ReceiptLongRounded} from "@mui/icons-material";
import ToastService from "../../../../../../../../../core/services/toast";
import PartnerIndustryRules from "../../../../../../../../../core/services/partner-industry-rules";
import {usePartnerInformation} from "../../../../../../../../hooks/use-partner-information";
import CacheService from "../../../../../../../../../core/services/cache/cache_service";


const billInfoSummaryBodyIds = {
    subTotal: 'subTotal',
    discount: 'discount',
    tax: (i) => `tax-${i}`,
    total: 'total',
    transaction: (i) => `transaction-${i}`,
}

const billInfoSummaryActionIds = {
    nextStep: 'nextStep',
    pay: "pay",
    closed: "closed",
    credits: 'credits',
    giftCard: 'giftCard',
    removeGiftCard: 'removeGiftCard',
    bypass: 'bypass',
}

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};

const QuoteInformationViewBillInfoSection = ({
                                                 order,
                                                 setOrder,
                                                 loading,
                                                 generateNextOrderEntity,
                                                 generatingNextOrderEntity,
                                                 removeGiftCard,
                                                 removeTransaction,
                                                 getOrderInformation,
                                                 payOrder,
                                                 customerId,
                                                 submittingDisabled,
                                                 setSubmittingDisabled,
                                             }) => {
    const params = useParams();
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const [openBypassDialog, setOpenBypassDialog] = useState(false);
    const [openAddGiftCardDialog, setOpenAddGiftCardDialog] = useState(false);
    const [openPayDialog, setOpenPayDialog] = useState(false);
    const [openCreditsDialog, setOpenCreditsDialog] = useState(false);
    const [updatingTransactions, setUpdatingTransactions] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const customer = useAppSelector(state => state.customerInformation);
    const partnerInformation = usePartnerInformation();

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const partnerId = useMemo(() => partnerInformation?.id ?? CacheService.getOperatorPartnerId(), [partnerInformation?.id],);
    const forceCustomersAsCod = useMemo(() => PartnerIndustryRules.forcesCustomersAsCod(partnerId), [partnerId],);
    const isNeededToPay = useMemo(() => PartnerIndustryRules.effectiveIsNeededToPay(order, partnerId), [order, partnerId]);

    const creditMemos = useMemo(() =>
            order?.appliedTransactions?.filter(e => e.transactionTypeName === AccountingTransactionTypes.CreditMemo) ?? [],
        [order?.appliedTransactions])

    /**
     * Adds the selected Credit to the order.
     * -if the result of the api is successful, Add the transaction to transactions list of the order info.
     * @type {function(*): Promise<void>}
     */
    const addCreditToOrder = useCallback(async (credit) => {
        const forApi = {
            customerId: customerId,
            transactionNo: credit.transactionNo,
            orderId: orderId,
        }
        setSubmittingDisabled(true);
        const response = await Api.addCreditToQuote(forApi);
        if (!isMounted())
            return;
        setSubmittingDisabled(false);
        if (response?.resultFlag) {
            setOrder(response.data);
        }
    }, [customerId, orderId, isMounted, setOrder, setSubmittingDisabled])

    /**
     * Updates the transactions through the given mutator.
     * @type {function(*=, ...[*]=): Promise<void>}
     */
    const updateTransaction = useCallback(async (mutator, ...args) => {
        setUpdatingTransactions(true);
        await mutator(...args);
        if (!isMounted())
            return;
        setUpdatingTransactions(false);
    }, [isMounted])

    /**
     * Gets users confirmation before removing the current order transaction entirely.
     * @type {function(*=): void}
     */
    const getRemoveTransactionConfirmation = useCallback((transaction) => {
        if (!Number.isFinite(Number(transaction?.id))) {
            ToastService.error({
                title: 'Refund failed',
                message: 'The transaction could not be refunded because its identifier is invalid.',
            });
            return;
        }
        ToastService.mainAPIRedZoneWarning();
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this transaction and
                    get {formatMoney(transaction?.amount ?? 0)} refunded to your account?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && updateTransaction(removeTransaction, transaction?.id),
        );
    }, [removeTransaction, updateTransaction])


    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: customerId,
            // TODO: receipt id for the print receipt
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [customerId])

    const billInfoSummaryBody = useMemo(() => {
        const res = [];
        const discountAmount = (order?.totalItemsDiscountAmount ?? 0);
        const transactions = order?.appliedTransactions?.filter(e => ![AccountingTransactionTypes.userOrder, AccountingTransactionTypes.invoice].includes(e.transactionTypeName)) ?? [];
        res.push({
            id: billInfoSummaryBodyIds.subTotal,
            title: 'Sub total',
            value: order?.subTotal,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        })
        if (discountAmount > 0) {
            res.push({
                id: billInfoSummaryBodyIds.discount,
                value: -discountAmount,
                title: 'Discount',
                loadingValue: "loading...",
                format: formatMoney,
                action: {show: false},
            })
        }
        let taxI = 0;
        for (const tax of order?.taxes ?? []) {
            res.push({
                id: billInfoSummaryBodyIds.tax(taxI++),
                title: <span className={classnames({'line-through': tax?.exemptNo?.length})}>
                    {tax.shortName ?? '--'} ({tax?.rate ?? '--'}%)
                </span>,
                value: tax?.exemptNo?.length ? `exempt: ${tax?.exemptNo}` : formatMoney(tax.total),
                loadingValue: "loading...",
                action: {show: false},
            })
        }
        res.push({
            id: billInfoSummaryBodyIds.total,
            title: 'Total',
            value: order?.total,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        });
        let transactionI = 0;
        for (const transaction of transactions) {
            const showReceipt = transaction?.transactionTypeName === AccountingTransactionTypes.payment && transaction.receiptId && operatorAccessLevels.packages.sales.paymentReceipts;
            res.push({
                id: billInfoSummaryBodyIds.transaction(transaction?.id ?? --transactionI),
                transaction,
                titleSuffix:
                    showReceipt
                        ? <IconButton
                            color={'paper.primary'}
                            className={'ml-1'}
                            onPressed={() => openReceiptDialog(transaction)}
                        >
                            <ReceiptLongRounded className={'icon small'}/>
                        </IconButton>
                        : null,
                title: transaction?.transactionTypeDescription,
                value: -(transaction?.amount ?? 0),
                loadingValue: "loading...",
                format: formatMoney,
                action: {
                    show: !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes,
                    className: 'ml-2',
                    submitting: updatingTransactions,
                    icon: <RefundIcon className={'icon small'}/>,
                    submittingIcon: <CircularProgress color={'secondary'} size={15}/>,
                    props: {color: 'paper.error'},
                    onClick: (entry) => getRemoveTransactionConfirmation(entry.transaction),
                },
            })
        }
        return res;
    }, [order?.totalItemsDiscountAmount, order?.appliedTransactions, order?.subTotal, order?.total, order?.taxes, order?.isClosed, operatorAccessLevels.packages.sales.paymentReceipts, operatorAccessLevels.packages.sales.manageQuotes, updatingTransactions, openReceiptDialog, getRemoveTransactionConfirmation])

    const billInfoSummaryActions = useMemo(() => [
        {
            id: billInfoSummaryActionIds.closed,
            value: 'Order closed',
            submittingValue: 'Submitting...',
            show: !!order && !!order?.isClosed,
            disabled: true,
            variant: 'contained',
            onClick: () => false,
        },
        {
            id: billInfoSummaryActionIds.nextStep,
            value: 'Generate Sales-order',
            submittingValue: 'Generating...',
            show: !!order && !order?.isClosed && !isNeededToPay && operatorAccessLevels.packages.sales.manageQuotes,
            disabled: loading || generatingNextOrderEntity || !order?.items?.length || submittingDisabled,
            variant: 'contained',
            onClick: () => generateNextOrderEntity(true),
        },
        {
            id: billInfoSummaryActionIds.pay,
            value: isNeededToPay ? 'Pay this order' : "Add payment anyways",
            submittingValue: 'Submitting...',
            show: !!order && !order?.isClosed && operatorAccessLevels.packages.sales.manageQuotes && (
                isNeededToPay
                || (!forceCustomersAsCod && order?.termsCode === CustomerPaymentTerms.COD && PartnerIndustryRules.hasOutstandingBalance(order))
                || (forceCustomersAsCod && !!order?.payInfo?.byPassed && PartnerIndustryRules.hasOutstandingBalance(order))
            ),
            disabled: loading || !order?.items?.length,
            variant: isNeededToPay ? 'contained' : "outlined",
            onClick: () => setOpenPayDialog(true),
        },
        {
            id: billInfoSummaryActionIds.credits,
            value: 'Add Credit',
            submittingValue: 'Submitting...',
            show: !!order && !order?.isClosed && !forceCustomersAsCod && order?.termsCode === CustomerPaymentTerms.COD && operatorAccessLevels.packages.sales.manageQuotes,
            disabled: loading || updatingTransactions || !order?.items?.length,
            variant: 'outlined',
            onClick: () => setOpenCreditsDialog(true),
        },
        {
            id: billInfoSummaryActionIds.giftCard,
            value: 'Add Gift card or Coupon',
            submittingValue: 'Submitting...',
            show: !!order && !order?.isClosed && !order?.appliedGiftCardInfo && !creditMemos.length && operatorAccessLevels.packages.sales.manageQuotes && operatorAccessLevels.packages.sales.giftCards,
            disabled: loading || updatingTransactions || !order?.items?.length,
            variant: 'outlined',
            onClick: () => setOpenAddGiftCardDialog(true),
        },
        {
            id: billInfoSummaryActionIds.removeGiftCard,
            value: 'Remove Gift card or Coupon',
            submittingValue: 'Submitting...',
            show: !!order && !order?.isClosed && !!order?.appliedGiftCardInfo && !creditMemos.length && operatorAccessLevels.packages.sales.manageQuotes && operatorAccessLevels.packages.sales.giftCards,
            disabled: loading || updatingTransactions || !order?.items?.length,
            variant: 'outlined',
            onClick: () => updateTransaction(removeGiftCard, order?.appliedGiftCardInfo),
        },
        {
            id: billInfoSummaryActionIds.bypass,
            value: 'Bypass this order',
            submittingValue: 'Submitting...',
            show: !!order && !order?.isClosed && !order?.payInfo?.byPassed && operatorAccessLevels.packages.sales.manageQuotes && operatorAccessLevels.packages.sales.quoteBypass,
            disabled: loading || !order?.items?.length,
            variant: 'text',
            onClick: () => setOpenBypassDialog(true),
        },
    ], [order, isNeededToPay, forceCustomersAsCod, operatorAccessLevels.packages.sales.manageQuotes, operatorAccessLevels.packages.sales.giftCards, operatorAccessLevels.packages.sales.quoteBypass, loading, generatingNextOrderEntity, submittingDisabled, updatingTransactions, creditMemos.length, generateNextOrderEntity, updateTransaction, removeGiftCard])

    const billInfoSummary = useMemo(() => ({
        header: {
            title: 'Balance',
            value: order?.balance,
            loadingValue: "Loading...",
            format: formatMoney,
        },
        body: billInfoSummaryBody,
        actions: billInfoSummaryActions,
    }), [billInfoSummaryBody, billInfoSummaryActions, order?.balance])


    return (
        <>
            {
                !order?.isClosed && operatorAccessLevels.packages.sales.quoteBypass &&
                <OrderBypassInfoCard
                    removable
                    loading={loading}
                    orderId={orderId}
                    openBypassDialog={openBypassDialog}
                    setOpenBypassDialog={setOpenBypassDialog}
                    orderNo={order?.orderNo}
                    getOrderInformation={getOrderInformation}
                    customer={customer}
                />
            }
            <SummaryActionCard
                className={'auto-height'}
                header={billInfoSummary.header}
                body={billInfoSummary.body}
                actions={billInfoSummary.actions}
                loadingCount={billInfoSummary.body.length}
                loading={loading}
            />
            <AddGiftCardToOrderDialog
                open={openAddGiftCardDialog}
                setOpen={setOpenAddGiftCardDialog}
                onSuccess={setOrder}
                orderId={orderId}
                customerId={customerId}
            />
            <AddCreditToOrderDialog
                customerId={customerId}
                open={openCreditsDialog}
                selectedCreditTransactionNos={creditMemos?.map(e => e.transactionNo) ?? []}
                close={() => setOpenCreditsDialog(false)}
                onAdd={addCreditToOrder}
                onRemove={(transaction) => updateTransaction(
                    removeTransaction,
                    creditMemos.find(e => e.transactionNo === transaction.transactionNo)?.id
                )}
            />
            <PayForOrderDialog
                open={openPayDialog}
                close={() => setOpenPayDialog(false)}
                pay={payOrder}
                customerId={customerId}
                remainingBalance={PartnerIndustryRules.effectiveRemainingBalance(order, partnerId)}
                showPreAuthMargin
            />
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
        </>
    )
}


export default QuoteInformationViewBillInfoSection;
